import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FadeInSection } from '../components/FadeInSection';

export function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Privacy policy for IKhanGynae — how we collect, use, protect and retain your personal and medical information in compliance with POPIA.'
      );
    }
  }, []);

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            Protection of Personal Information Act (POPIA)
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            Privacy Policy
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
          <p
            className="mt-4 text-sm font-body"
            style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--font-body)' }}
          >
            Effective date: 1 January 2025 · Last reviewed: March 2026
          </p>
        </div>
      </section>

      {/* ── Intro ── */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <p
              className="font-body text-base leading-relaxed mb-4"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
            >
              IKhanGynae (the practice of Dr Imraan Khan FCOG(SA), JKD at Melomed Gatesville &amp; Melomed Mitchell's Plain,
              Cape Town, Western Cape) is the responsible party for the personal information it processes.
              This Privacy Policy explains how we collect, use, store, and protect your personal and medical
              information in compliance with the Protection of Personal Information Act 4 of 2013 (POPIA) and
              the ethical requirements of the Health Professions Council of South Africa (HPCSA).
            </p>
            <p
              className="font-body text-base leading-relaxed"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
            >
              By registering as a patient, submitting an enquiry, or using this website, you consent to the
              collection and processing of your personal information as described in this policy. You may withdraw
              your consent at any time subject to legal and medical-record retention obligations.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── What We Collect ── */}
      <section className="py-14 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--color-navy)' }}
            >
              1. What Personal Information We Collect
            </h2>

            <PolicySubhead>Identity & contact information</PolicySubhead>
            <PolicyList items={[
              'Full name, date of birth, gender, and South African identity number',
              'Residential address, telephone number, and email address',
              'Next-of-kin or emergency contact details',
              'Medical aid membership details and scheme name',
            ]} />

            <PolicySubhead>Clinical information</PolicySubhead>
            <PolicyList items={[
              'Medical history, current symptoms, and reason for consultation',
              'SOAP notes, consultation records, and clinical assessments',
              'Diagnosis codes (ICD-10), procedure codes (NHRPL), and surgical records',
              'Prescriptions, medication history, and drug formulary records',
              'Obstetric history, antenatal records, and neonatal information where applicable',
              'Laboratory and imaging results shared with the practice',
            ]} />

            <PolicySubhead>Financial information</PolicySubhead>
            <PolicyList items={[
              'Invoice and payment records',
              'Medical aid claim submissions and remittance advice',
              'Account balance and statement history',
            ]} />

            <PolicySubhead>Website usage information</PolicySubhead>
            <PolicyList items={[
              'IP address, browser type, and pages visited (collected via Google Analytics)',
              'Information submitted through the online booking or registration forms',
            ]} />
          </FadeInSection>
        </div>
      </section>

      {/* ── How We Use It ── */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--color-navy)' }}
            >
              2. How We Use Your Information
            </h2>
            <p
              className="font-body text-base leading-relaxed mb-4"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
            >
              We process your personal information only for the following purposes:
            </p>
            <PolicyList items={[
              'Providing obstetric and gynaecological medical care, including diagnosis and treatment',
              'Scheduling and managing appointments and surgical bookings',
              'Preparing and issuing invoices, processing payments, and submitting medical aid claims',
              'Communicating with you regarding your care, results, follow-up appointments, and reminders (via phone, email, or WhatsApp)',
              'Compiling clinical reports and referral letters for other healthcare providers involved in your care',
              'Complying with legal and regulatory obligations, including mandatory reporting requirements',
              'Statistical and quality-improvement purposes using de-identified data only',
              'Responding to enquiries, booking requests, or referrals submitted via this website',
            ]} />
            <p
              className="font-body text-sm leading-relaxed mt-4"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.65 }}
            >
              We do not use your personal information for direct marketing without your explicit consent, and we
              will never sell, rent, or trade your information to third parties.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── Sharing ── */}
      <section className="py-14 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--color-navy)' }}
            >
              3. Who We Share Your Information With
            </h2>
            <p
              className="font-body text-base leading-relaxed mb-4"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
            >
              Your information is shared only where clinically or legally necessary:
            </p>
            <PolicyList items={[
              'Medical aids and health insurers — to process claims and reimbursements on your behalf',
              'Hospitals, theatre staff, and anaesthetists — when arranging or performing surgical procedures',
              'Other specialists or healthcare providers — only with your explicit consent for referral or co-management',
              'Laboratories and diagnostic facilities — to order and receive test results',
              'The South African Information Regulator, HPCSA, or other regulatory bodies — where required by law',
              'Google (Analytics) — anonymised website usage data only; no medical information is shared',
            ]} />
            <p
              className="font-body text-sm leading-relaxed mt-4"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.65 }}
            >
              Any third party that processes your information on our behalf is required to comply with POPIA and
              to implement equivalent data security measures.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── Retention ── */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--color-navy)' }}
            >
              4. How Long We Retain Your Information
            </h2>

            <div
              className="rounded-xl p-6 mb-6 border-l-4"
              style={{ backgroundColor: 'var(--color-primary-light)', borderLeftColor: 'var(--color-primary)' }}
            >
              <p
                className="font-body text-sm leading-relaxed"
                style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
              >
                <strong>HPCSA requirement:</strong> Clinical and patient records must be retained for a minimum
                of <strong>6 years</strong> from the date of the last entry, or until the patient turns 21 years
                of age (whichever is longer). Obstetric records relating to a child are retained until that
                child reaches the age of 21.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { category: 'Clinical records (SOAP notes, prescriptions, surgeries)', retention: '6 years minimum (HPCSA) — never hard-deleted' },
                { category: 'Obstetric records for a minor patient', retention: 'Until the child turns 21 years of age' },
                { category: 'Financial records (invoices, payments, claims)', retention: '5 years minimum (South African Revenue Service requirement)' },
                { category: 'Audit logs and access records', retention: '6 years — append-only, never deletable' },
                { category: 'Website enquiry and registration forms', retention: '2 years, or until the enquiry is resolved and a patient record created' },
                { category: 'Google Analytics data', retention: '14 months (Google default retention setting)' },
              ].map(({ category, retention }) => (
                <div
                  key={category}
                  className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-3 border-b"
                  style={{ borderColor: '#e5e7eb' }}
                >
                  <span
                    className="font-body text-sm font-medium flex-1"
                    style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
                  >
                    {category}
                  </span>
                  <span
                    className="font-body text-sm flex-1"
                    style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.7 }}
                  >
                    {retention}
                  </span>
                </div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── Security ── */}
      <section className="py-14 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--color-navy)' }}
            >
              5. How We Protect Your Information
            </h2>
            <p
              className="font-body text-base leading-relaxed mb-4"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
            >
              We implement appropriate technical and organisational safeguards to protect your information against
              unauthorised access, loss, or disclosure:
            </p>
            <PolicyList items={[
              'All data is stored in Google Cloud Firestore hosted in the Africa (Johannesburg) region — no data leaves South Africa',
              'Access to the practice portal requires two-factor authentication (email and SMS OTP)',
              'Role-based access controls ensure only authorised staff can access clinical information',
              'Every access to a patient record is logged with a timestamp and user identity in a tamper-proof audit log',
              'All communications between your browser and the practice server use TLS encryption (HTTPS)',
              'Patient identifiers are never included in AI-assisted processing (ICD-10 coding, report drafting)',
              'Document uploads and clinical files are stored in private, access-controlled cloud storage',
            ]} />
          </FadeInSection>
        </div>
      </section>

      {/* ── Your Rights ── */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--color-navy)' }}
            >
              6. Your Rights Under POPIA
            </h2>
            <p
              className="font-body text-base leading-relaxed mb-6"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
            >
              As a data subject under POPIA, you have the following rights. To exercise any of these rights,
              contact us using the details in Section 7.
            </p>
            <div className="space-y-4">
              {[
                {
                  right: 'Right of access',
                  detail: 'You may request a copy of the personal information we hold about you at any time.',
                },
                {
                  right: 'Right to correction',
                  detail: 'You may request that inaccurate, incomplete, or misleading personal information be corrected or updated.',
                },
                {
                  right: 'Right to erasure',
                  detail: 'You may request deletion of your information where it is no longer necessary for its original purpose. Note that clinical records cannot be deleted before the mandatory retention period has elapsed.',
                },
                {
                  right: 'Right to object',
                  detail: 'You may object to the processing of your personal information for direct marketing or other purposes not directly related to your medical care.',
                },
                {
                  right: 'Right to restriction',
                  detail: 'You may request that we restrict processing of your information where you contest its accuracy or where processing is unlawful.',
                },
                {
                  right: 'Right to data portability',
                  detail: 'You may request that your records be transferred to another healthcare provider in a structured, machine-readable format.',
                },
                {
                  right: 'Right to lodge a complaint',
                  detail: 'If you believe we have processed your information unlawfully, you have the right to lodge a complaint with the Information Regulator of South Africa (inforeg.org.za).',
                },
              ].map(({ right, detail }) => (
                <div
                  key={right}
                  className="rounded-lg p-5 border"
                  style={{ borderColor: '#e5e7eb', backgroundColor: '#fafafa' }}
                >
                  <p
                    className="font-body font-semibold text-sm mb-1"
                    style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
                  >
                    {right}
                  </p>
                  <p
                    className="font-body text-sm leading-relaxed"
                    style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.8 }}
                  >
                    {detail}
                  </p>
                </div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── Contact & Information Officer ── */}
      <section className="py-14 px-4" style={{ backgroundColor: 'var(--color-navy)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <FadeInSection>
            <p
              className="font-heading italic mb-2"
              style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
            >
              Data queries and requests
            </p>
            <h2
              className="font-heading mb-6 text-white"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 300, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}
            >
              7. Contact Us
            </h2>
            <p
              className="font-body text-sm leading-relaxed mb-8"
              style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--font-body)' }}
            >
              For any questions about this Privacy Policy, to exercise your POPIA rights, or to report a
              concern about how your information is handled, contact our Information Officer:
            </p>
            <div
              className="inline-block rounded-xl p-8 text-left"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              <p
                className="font-heading text-lg mb-1"
                style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)' }}
              >
                Dr Imraan Khan
              </p>
              <p
                className="font-body text-sm mb-1"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--font-body)' }}
              >
                Information Officer · IKhanGynae
              </p>
              <p
                className="font-body text-sm mb-1"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--font-body)' }}
              >
                JKD at Melomed Gatesville &amp; Melomed Mitchell's Plain
              </p>
              <p
                className="font-body text-sm mb-4"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--font-body)' }}
              >
                Cape Town, Western Cape
              </p>
              <a
                href="mailto:drikhan@ikhangynae.co.za"
                className="font-body text-sm font-medium"
                style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-body)' }}
              >
                drikhan@ikhangynae.co.za
              </a>
            </div>

            <div
              className="mt-10 rounded-lg p-4 text-left"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <p
                className="font-body text-xs leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-body)' }}
              >
                <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Information Regulator of South Africa</strong>
                <br />
                JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001
                <br />
                <a href="https://www.inforeg.org.za" target="_blank" rel="noopener noreferrer" className="underline">www.inforeg.org.za</a>
                {' · '}
                <a href="mailto:inforeg@justice.gov.za" className="underline">inforeg@justice.gov.za</a>
              </p>
            </div>

            <p
              className="font-body text-xs mt-8"
              style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-body)' }}
            >
              We reserve the right to update this Privacy Policy. The effective date at the top of this page
              will reflect the date of the most recent revision. Material changes will be communicated to
              registered patients by email.
            </p>

            <Link
              to="/contact"
              className="inline-block mt-8 px-8 py-3 rounded-full font-body font-semibold text-sm transition-opacity hover:opacity-80"
              style={{
                backgroundColor: 'var(--color-gold)',
                color: 'var(--color-navy)',
                fontFamily: 'var(--font-body)',
              }}
            >
              Contact the practice
            </Link>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
}

/* ── Small helper components ── */

function PolicySubhead({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="font-body font-semibold text-sm uppercase tracking-wide mt-6 mb-2"
      style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)', letterSpacing: '0.07em' }}
    >
      {children}
    </h3>
  );
}

function PolicyList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 font-body text-sm leading-relaxed"
          style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)', opacity: 0.85 }}
        >
          <span
            className="mt-1.5 flex-shrink-0 rounded-full"
            style={{ width: 6, height: 6, backgroundColor: 'var(--color-gold)' }}
          />
          {item}
        </li>
      ))}
    </ul>
  );
}
